/**
 * Waitlist resource.
 * Public endpoints (joinBrand, verify) require no auth.
 * Admin endpoints (fetchBrands, fetchCreators, approve, disapprove) are admin-only.
 *
 * All admin list endpoints use WritePaginated → PaginatedResponse<T>.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse, PaginatedResponse } from '@discovr/common';

/** How a newly-approved brand owner receives their login credentials —
 *  matches the BE `CredentialDelivery` type 1:1. */
export type CredentialDelivery = 'link' | 'password';

/**
 * Brand waitlist entry — matches the BE admin read model `BrandWaitlistRow`
 * (internal/admin/model.go) exactly, since /waitlist/brands/{tab} serves that
 * struct verbatim via WritePaginated. Field names MUST stay in sync with the
 * BE JSON tags — the FE reads these directly off the response (no remap).
 *
 * NOTE: the BE row does not currently include `message` or `heard_about_us`
 * (they aren't persisted on the brand entry), and socials come back as
 * `social_platform` / `social_handle`, not a combined `brand_socials`.
 */
export interface WaitlistEntry {
  id: string;
  email: string;
  first_name: string;
  last_name?: string;
  company_name: string;
  website?: string;
  social_platform?: string;
  social_handle?: string;
  email_verified?: boolean;
  /** Not currently returned by the BE brand-waitlist row — always undefined until the BE surfaces it. */
  message?: string;
  status: 'pending' | 'approved' | 'disapproved';
  /** Only present on the approved tab: 'pending' | 'expired' | 'used' | 'none'. */
  activation_status?: 'pending' | 'expired' | 'used' | 'none';
  created_at: string;
  updated_at?: string;
}

export interface CreatorWaitlistEntry {
  id: string;
  email: string;
  name: string;
  platform?: string;
  handle?: string;
  status: 'pending' | 'approved' | 'disapproved';
  created_at: string;
}

export interface WaitlistListResponse<T> {
  entries: T[];
  total: number;
}

export interface WaitlistVerifyResult {
  email_verified: boolean;
  message: string;
}

/** Result of the pre-submit email sendability check (BE /waitlist/validate-email). */
export interface ValidateEmailResult {
  /** false ⇒ block submit. */
  valid: boolean;
  /** true ⇒ the domain check couldn't complete; caller should allow (fail-open). */
  inconclusive: boolean;
  /** ok | invalid_format | synthetic | blocked_domain | no_mail_domain | inconclusive */
  reason: string;
}

export const waitlist = {
  // ─── Public (no auth) ─────────────────────────────────────────────────────

  /** Brand joins the waitlist (public, pre-login). */
  joinBrand: (data: {
    email: string;
    first_name: string;
    last_name?: string;
    company_name: string;
    website?: string;
    heard_about_us?: string;
    message?: string;
  }): Promise<void> => apiClient.post('/waitlist/brand', data).then(() => undefined),

  /**
   * Validate an email's sendability before submit (public, pre-login). Returns
   * {valid, inconclusive, reason}. Pass an AbortSignal to cancel a stale check.
   */
  validateEmail: (email: string, signal?: AbortSignal): Promise<ValidateEmailResult> =>
    apiClient
      .post<DataResponse<ValidateEmailResult>>('/waitlist/validate-email', { email }, { signal })
      .then((r) => r.data.data),

  /** Verify brand waitlist email via token (public, from email link). */
  verify: (email: string, token: string): Promise<WaitlistVerifyResult> => {
    const params = new URLSearchParams({ email, token });
    return apiClient
      .get<DataResponse<WaitlistVerifyResult>>(`/waitlist/verify?${params}`)
      .then((r) => r.data.data);
  },

  // ─── Admin-only ────────────────────────────────────────────────────────────

  /**
   * Fetch brand waitlist entries (admin).
   * BE: WritePaginated → { data: WaitlistEntry[], pagination: {...} }
   */
  fetchBrands: (
    tab: 'pending' | 'approved' | 'disapproved',
  ): Promise<WaitlistListResponse<WaitlistEntry>> =>
    apiClient
      .get<PaginatedResponse<WaitlistEntry>>(`/waitlist/brands/${tab}`)
      .then((r) => ({ entries: r.data.data, total: r.data.pagination.total })),

  /**
   * Fetch creator waitlist entries (admin).
   * BE: WritePaginated → { data: CreatorWaitlistEntry[], pagination: {...} }
   */
  fetchCreators: (
    tab: 'pending' | 'approved' | 'disapproved',
    page?: number,
    limit?: number,
  ): Promise<WaitlistListResponse<CreatorWaitlistEntry>> =>
    apiClient
      .get<PaginatedResponse<CreatorWaitlistEntry>>(`/waitlist/creators/${tab}`, {
        params: { page, limit },
      })
      .then((r) => ({ entries: r.data.data, total: r.data.pagination.total })),

  /**
   * Approve a brand waitlist entry (admin), choosing how the owner receives
   * their login credentials: a self-service set-password link, or an
   * immediately-usable generated password (returned here so the admin can
   * see/copy it once). The FE never stores it beyond this response, but it
   * does transiently persist server-side in the email outbox until the
   * notification email is sent (or TTL-reaped) — not "never persisted".
   */
  approveBrand: (
    waitlistId: string,
    credentialsEmail: string,
    delivery: CredentialDelivery,
  ): Promise<{ message: string; generated_password?: string }> =>
    apiClient
      .post<DataResponse<{ message: string; generated_password?: string }>>(
        `/waitlist/${encodeURIComponent(waitlistId)}/approve-brand`,
        { credentials_email: credentialsEmail, credential_delivery: delivery },
      )
      .then((r) => r.data.data),

  /**
   * Resend an approved brand owner's set-password (activation) link, invalidating
   * the previous one (super-admin only — `brand:activation:resend`). 200 → message.
   */
  resendBrandActivation: (waitlistId: string): Promise<void> =>
    apiClient
      .post(`/waitlist/${encodeURIComponent(waitlistId)}/resend-brand-activation`)
      .then(() => undefined),

  /** Disapprove a waitlist entry (brand or creator). WriteNoContent → 204. */
  disapproveBrand: (waitlistId: string): Promise<void> =>
    apiClient.post(`/waitlist/${encodeURIComponent(waitlistId)}/disapprove`).then(() => undefined),

  /** Approve a creator waitlist entry (admin). WriteNoContent → 204. */
  approveCreator: (entryId: string): Promise<void> =>
    apiClient
      .post(`/waitlist/${encodeURIComponent(entryId)}/approve-creator`)
      .then(() => undefined),

  /** Disapprove a creator waitlist entry (admin). WriteNoContent → 204. */
  disapproveCreator: (entryId: string): Promise<void> =>
    apiClient.post(`/waitlist/${encodeURIComponent(entryId)}/disapprove`).then(() => undefined),
};
